// Sidebar.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Toolbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  BarChart,
  Construction,
  Feed,
  Handyman,
  HelpOutline,
  Home,
  MonitorHeart,
  RequestQuote,
  Sell,
  SettingsSuggest,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { ROUTES } from "../Routes/routes";
import logger from "../../helpers/logger";
import { useUserRoles } from '../../hooks/useUserRole';
import { hasPageAccess } from "../../helpers/check-role-permission";

// Constants for sidebar widths
const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 80;

// Tab definitions for the sidebar
const TABS = [
  { label: "Home", icon: <Home />, route: ROUTES.home },
  {
    label: "Rack Pricing",
    icon: <RequestQuote />,
    route: ROUTES.rackPricingFeatures,
  },
  { label: "Rack Features", icon: <Construction />, route: ROUTES.rackFeatures },
  { label: "Bid Pricing", icon: <Sell />, route: ROUTES.bidPricingFeatures },
  { label: "Bid Features", icon: <Handyman />, route: ROUTES.bidFeatures },
  { label: "Reports", icon: <BarChart />, route: ROUTES.reports },
  { label: "Setup", icon: <SettingsSuggest />, route: ROUTES.setup },
  { label: "Monitoring", icon: <MonitorHeart />, route: ROUTES.quartz },
  { label: "Logs", icon: <Feed />, route: ROUTES.logs },
  { 
    label: "Help", 
    icon: <HelpOutline />, 
    externalLink: "https://servicedesk.tristarservices.com/a/catalog/request-items/149",
    ariaLabel: "Submit Help for Rackstar (opens in new tab)"
  },
];

const SB_SESSION_KEY = "lP"; // Last Path session storage key

const Sidebar = ({ isSidebarOpen, toggleSidebar }) => {
  const { roles } = useUserRoles();
  const [selectedPath, setSelectedPath] = useState(sessionStorage.getItem(SB_SESSION_KEY) || window.location.pathname);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

   // Filter tabs based on user roles
  const visibleTabs = useMemo(() => {
    return TABS.filter(tab => {
     if (tab.externalLink) return true; // Always show external links
     var hasAccess = hasPageAccess(roles, tab.route);
     return tab.route ?  hasAccess : true;
   })
  },[roles]);

  // JSX render for the sidebar
  return (
    <Drawer
      variant={isSmallScreen ? "temporary" : "permanent"}
      open={isSidebarOpen}
      onClose={isSmallScreen ? toggleSidebar : undefined}
      sx={{
        width: isSidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        transition: "width 0.3s ease-in-out",
        fontFamily: "'Ubuntu-Medium', sans-serif",
        [`& .MuiDrawer-paper`]: {
          width: isSidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden", // Prevent horizontal scrollbar
          overflowY: "auto", // Allow vertical scrolling if needed
          fontFamily: "'Ubuntu-Medium', sans-serif",
        },
      }}
    >
      <Toolbar /> {/* Spacer for the fixed AppBar */}
      <Box
        sx={{
          paddingTop: !isSmallScreen ? "10px" : "0px",
          overflowX: "hidden",
          fontFamily: "'Ubuntu-Medium', sans-serif",
        }}
      >
        <List sx={{ paddingTop: "0px" }}>
          {visibleTabs.map((tab) => (
            <ListItem key={tab.label} disablePadding sx={{ display: "block" }}>
              <Tooltip
                title={tab.label}
                placement="right"
                arrow
                disableHoverListener={isSidebarOpen}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "1rem",
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (tab.externalLink) {
                      logger.log(`Opening external link: ${tab.externalLink}`);
                      window.open(tab.externalLink, "_blank", "noopener,noreferrer");
                      if (isSmallScreen) {
                        toggleSidebar(false);
                      }
                    } else {
                      logger.log(`Navigating to ${tab.route}`);
                      if (isSmallScreen) {
                        toggleSidebar(false);
                      }
                      sessionStorage.setItem(SB_SESSION_KEY, tab.route);
                      setSelectedPath(tab.route);
                      navigate(tab.route);
                    }
                  }}
                  selected={selectedPath === tab.route}
                  aria-label={!isSidebarOpen ? (tab.ariaLabel || tab.label) : undefined} // Only when collapsed
                  component={tab.externalLink ? "a" : "div"}
                  href={tab.externalLink || undefined}
                  target={tab.externalLink ? "_blank" : undefined}
                  rel={tab.externalLink ? "noopener noreferrer" : undefined}
                  sx={{
                    justifyContent: isSidebarOpen ? "initial" : "center",
                    px: isSidebarOpen ? 2.5 : 2, // Add 2 units (16px) padding-left in collapsed state
                    minWidth: 0, // Ensure button fits within collapsed width
                    "&.Mui-selected": {
                      backgroundColor: "rgba(25, 118, 210, 0.2)",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.3)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0, // Remove default min-width to fit collapsed state
                      mr: isSidebarOpen ? 3 : 0, // Adjust margin based on state
                      justifyContent: "center",
                    }}
                    aria-hidden="true"
                  >
                    {tab.icon}
                  </ListItemIcon>
                  {isSidebarOpen && (
                    <ListItemText
                      primary={tab.label}
                      sx={{
                        opacity: isSidebarOpen ? 1 : 0,
                        whiteSpace: "nowrap", // Prevent text wrapping
                        overflow: "hidden", // Hide text overflow
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

Sidebar.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
};

export default Sidebar;
